import { makeAutoObservable } from "mobx";

export type ActivityType = "query" | "mutation" | "processing";

export interface ActivityPolicy {
  manualCancellable: boolean;   // Can user manually cancel?
  autoCancellable: boolean;      // Auto-cancel when navigating away?
  blockNavigation: boolean;      // Block navigation while active?
}

const ACTIVITY_POLICIES: Record<ActivityType, ActivityPolicy> = {
  query: {
    manualCancellable: true,
    autoCancellable: true,
    blockNavigation: false,
  },
  mutation: {
    manualCancellable: false,
    autoCancellable: false,
    blockNavigation: true,
  },
  processing: {
    manualCancellable: true,
    autoCancellable: false,
    blockNavigation: false,
  },
};

export interface ActivityDetail {
  id: string;
  type: ActivityType;
  operation: string;
  /** Qué se está haciendo, para mostrarlo (p. ej. el nombre del archivo que se sube). */
  detail?: string;
  entity?: string;
  startedAt: number;
  controller: AbortController;
  pathname?: string;
  presentation?: "page" | "overlay";
  policy: ActivityPolicy;
}

export interface ActivityEvent {
  timestamp: number;
  eventType: "START" | "END" | "CANCEL";
  activityId: string;
  type: ActivityType;
  operation: string;
  pathname?: string;
  presentation?: "page" | "overlay";
  duration?: number;
}

export class ActivityStore {
  private activities = new Map<string, ActivityDetail>();
  private eventHistory: ActivityEvent[] = [];
  private readonly MAX_EVENTS = 50;

  constructor() {
    makeAutoObservable(this);
  }

  private addEvent(event: ActivityEvent): void {
    this.eventHistory.unshift(event);
    if (this.eventHistory.length > this.MAX_EVENTS) {
      this.eventHistory = this.eventHistory.slice(0, this.MAX_EVENTS);
    }
  }

  get recentEvents(): ActivityEvent[] {
    return this.eventHistory;
  }

  /**
   * @param options una actividad puede ser cancelable a mano aunque su tipo no
   * lo sea: una subida es una mutación, pero mientras sube no cambió nada en el
   * servidor y cortarla es seguro. `detail` es lo que se muestra de ella.
   */
  startActivity(
    type: ActivityType = "query",
    operation?: string,
    pathname?: string,
    presentation?: "page" | "overlay",
    options?: { manualCancellable?: boolean; detail?: string }
  ): { activityId: string; signal: AbortSignal } {
    const id = crypto.randomUUID();
    const controller = new AbortController();
    const policy =
      options?.manualCancellable === undefined
        ? ACTIVITY_POLICIES[type]
        : { ...ACTIVITY_POLICIES[type], manualCancellable: options.manualCancellable };

    this.activities.set(id, {
      id,
      type,
      operation: operation || "unknown",
      detail: options?.detail,
      startedAt: Date.now(),
      controller,
      pathname,
      presentation,
      policy,
    });

    this.addEvent({
      timestamp: Date.now(),
      eventType: "START",
      activityId: id,
      type,
      operation: operation || "unknown",
      pathname,
      presentation,
    });

    return {
      activityId: id,
      signal: controller.signal,
    };
  }

  endActivity(id: string): void {
    const activity = this.activities.get(id);
    if (activity) {
      const duration = Date.now() - activity.startedAt;
      this.addEvent({
        timestamp: Date.now(),
        eventType: "END",
        activityId: id,
        type: activity.type,
        operation: activity.operation,
        pathname: activity.pathname,
        presentation: activity.presentation,
        duration,
      });
    }
    this.activities.delete(id);
  }

  /**
   * Manually cancel an activity (user clicks [X] in badge)
   */
  manualCancel(activityId: string): void {
    const activity = this.activities.get(activityId);

    if (!activity) {
      return;
    }

    if (!activity.policy.manualCancellable) {
      return;
    }

    this.addEvent({
      timestamp: Date.now(),
      eventType: "CANCEL",
      activityId: activity.id,
      type: activity.type,
      operation: activity.operation,
      pathname: activity.pathname,
      presentation: activity.presentation,
      duration: Date.now() - activity.startedAt,
    });

    activity.controller.abort();
    this.activities.delete(activityId);
  }

  /**
   * Auto-cancel all cancelable activities for a pathname (when navigating away)
   */
  cancelAllInPathname(pathname: string, presentation?: "page" | "overlay"): void {
    this.activities.forEach((activity) => {
      if (
        activity.pathname === pathname &&
        activity.presentation === presentation &&
        activity.policy.autoCancellable
      ) {
        this.addEvent({
          timestamp: Date.now(),
          eventType: "CANCEL",
          activityId: activity.id,
          type: activity.type,
          operation: activity.operation,
          pathname: activity.pathname,
          presentation: activity.presentation,
          duration: Date.now() - activity.startedAt,
        });

        activity.controller.abort();
        this.activities.delete(activity.id);
      }
    });
  }

  /**
   * Check if navigation is allowed from a pathname
   * (returns false if there are blocking operations)
   */
  canNavigateFromPathname(pathname: string, presentation?: "page" | "overlay"): boolean {
    const blockingActivities = Array.from(this.activities.values()).filter(
      (a) =>
        a.pathname === pathname &&
        a.presentation === presentation &&
        a.policy.blockNavigation
    );

    if (blockingActivities.length > 0) {
      return false;
    }

    return true;
  }

  get activeOperations(): ActivityDetail[] {
    return Array.from(this.activities.values());
  }

  get hasActiveOperations(): boolean {
    return this.activities.size > 0;
  }

  get activeCount(): number {
    return this.activities.size;
  }

  getActivityCount(type: ActivityType): number {
    return Array.from(this.activities.values()).filter(
      (a) => a.type === type
    ).length;
  }

  /**
   * @deprecated Use manualCancel instead
   */
  cancelOperation(id: string): void {
    this.manualCancel(id);
  }
}
